import API_SERVICE from '@Service/apiService';
import { getAPIByVersion } from '@Service/versionService';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import { STORAGE_TYPE, setInStorage } from '@Service/storageService';
import { isAfterOrEqual } from '@Utils/versionUtils';
import { useOriginalValue, cleanMetadataValue } from '@Utils/documentUtils';
import { DocumentStatus } from '@Constants/status';
import { guid } from '@Utils/commonUtils';
import store from '@Store';
import { refreshMetaData, abortAnimation } from './documentActions';
import {
    DOCUMENT_SCORECARD,
    NOTIFICATION_HANDLER,
    DOCUMENT_METADATA,
    MIX_PANEL
} from '@Constants/types';
import mixpanelEvents from '@Mixpanel';

const FAVORITES_API = '/v1/digitalappuserfavorites';

export function startSLEProcess(version, digitalAppId, digitalAppName, documentId) {
    return dispatch => {
        addLog(
            LOG_TYPE.INFO,
            'scoreCardActions.startSLEProcess',
            `DigiApp Version=${version} on digApp=${digitalAppId} for docuId=${documentId}`
        );

        const payload = {
            all: false,
            ids: [documentId],
            overrideUserData: true,
            query: {
                searchProperties: {
                    keyword: ''
                }
            }
        };

        // endpoint url: /seal-digapp/v1/digitalapps/{digAppId}/start
        const versionedEndPointUrl = getAPIByVersion('startSLEProcess', version);
        const endPointUrl = versionedEndPointUrl.replace('{digAppId}', digitalAppId);
        addLog(LOG_TYPE.INFO, 'scoreCardActions.startSLEProcess', endPointUrl);

        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');
        API_SERVICE.DIGAPP_API({
            method: 'post',
            url: endPointUrl,
            data: payload
        })
            .then(() => {
                setInStorage('sleProcessStarted', true, STORAGE_TYPE.PROPERTY_BAG);
                dispatch({ type: DOCUMENT_SCORECARD.SLE_PROCESS_STARTED });
                dispatch({
                    type: MIX_PANEL.MIXPANEL_DATA_SCORECARD_STEP_TIME,
                    payload: 'generating'
                });
                mixpanelEvents.analyzingstepcomplete();
            })
            .catch(err => {
                if (err.response && err.response.status && err.response.status === 400) {
                    // remove digapp from the digApp selector menu
                    store.dispatch({
                        type: DOCUMENT_SCORECARD.REMOVE_RULELESS_DIGAPPS,
                        payload: digitalAppName
                    });

                    abortAnimation();

                    addLog(
                        LOG_TYPE.ERROR,
                        'scoreCardActions.startSLEProcess',
                        `Analyzer ${digitalAppName} no longer available`
                    );

                    store.dispatch({
                        type: NOTIFICATION_HANDLER.WARNING,
                        payload: `Analyzer "${digitalAppName}" no longer available`
                    });
                }
            });
    };
}

export function setDigAppFlags(digAppInput) {
    return dispatch =>
        dispatch({
            type: DOCUMENT_SCORECARD.UPDATE_DIGAPP,
            payload: digAppInput
        });
}

export function getDigApps(version) {
    try {
        addLog(LOG_TYPE.INFO, 'scoreCardActions.getDigApps', `DigiApp Version=${version}`);
        return dispatch => {
            dispatch({ type: DOCUMENT_SCORECARD.FETCH_DIGAPP_REQUEST });
            API_SERVICE.refresh();
            API_SERVICE.setContentType('application/json');
            API_SERVICE.DIGAPP_API.get(getAPIByVersion('digitalapps', version))
                .then(res => {
                    addLog(LOG_TYPE.INFO, 'scoreCardActions.getDigApps', res.data);

                    const digAppsPendingStatusCheck = [];
                    if (res && res.data && res.data.data && res.data.data.length > 0) {
                        res.data.data.forEach(digApp => {
                            digAppsPendingStatusCheck.push(digApp.name);
                        });
                    }

                    dispatch({
                        type: DOCUMENT_SCORECARD.FETCH_DIGAPP_SUCCESS,
                        payload: {
                            digAppsFilteredArray: res.data.data,
                            enabled: isDigAppsEnabled(res),
                            digAppsPendingStatus: digAppsPendingStatusCheck
                        }
                    });
                })
                .catch(err => handleDigAppError(err, 'scoreCardActions.getDigApps'));
        };
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'scoreCardActions.getDigApps', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return null;
    }
}

export function getDigAppsPromise(version, result, userId) {
    // eslint-disable-next-line
    const promise = new Promise((resolve, reject) => {
        try {
            addLog(LOG_TYPE.INFO, 'scoreCardActions.getDigApps', `DigiApp Version=${version}`);
            store.dispatch({ type: DOCUMENT_SCORECARD.FETCH_DIGAPP_REQUEST });
            API_SERVICE.refresh();
            API_SERVICE.setContentType('application/json');

            API_SERVICE.DIGAPP_API({
                timeout: 5000,
                method: 'GET',
                url: getAPIByVersion('digitalapps', version)
            })

                .then(async res => {
                    addLog(LOG_TYPE.INFO, 'scoreCardActions.getDigApps', res.data);

                    const isVersionAfter = isAfterOrEqual('7.6.0.0', version);

                    let digAppsPendingStatusCheck = [];
                    let digApps = [];

                    let favorites = isAfterOrEqual('8.1.0.0', version)
                        ? await getFavorites(userId)
                        : [];

                    if (res && res.data && res.data.data && res.data.data.length > 0) {
                        res.data.data = res.data.data.map(x => ({
                            ...x,
                            isFavorite: favorites.includes(x.id)
                        }));

                        digApps = filterForActiveAnalyzers(version, res.data.data);
                        digAppsPendingStatusCheck = isVersionAfter ? [] : res.data.data;
                    }

                    store.dispatch({
                        type: DOCUMENT_SCORECARD.FETCH_DIGAPP_SUCCESS,
                        payload: {
                            digAppsFilteredArray: digApps,
                            enabled: isDigAppsEnabled(res),
                            malConfigured: malConfigurationCheck(res),
                            digAppsPendingStatus: digAppsPendingStatusCheck
                        }
                    });
                    // need to dispatch this action regardless the backend version to create the digAppSelector
                    fetchAnalyzersDetails(version, digApps);
                    resolve({ ...res, ...result });
                })

                .catch(err => {
                    if (err.toString().includes('timeout')) {
                        store.dispatch({
                            type: NOTIFICATION_HANDLER.WARNING,
                            payload: 'Unable to connect to scorecard service'
                        });
                    }

                    resolve({ ...result });
                    return handleDigAppError(err, 'scoreCardActions.getDigApps');
                });
        } catch (err) {
            reject(err);
            addLog(LOG_TYPE.ERROR, 'scoreCardActions.getDigApps', err);
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });
            return null;
        }
    });
    return promise;
}

async function fetchAnalyzersDetails(version, analyzers) {
    try {
        if (analyzers.length) {
            const requests = analyzers.map(analyzer =>
                fetchAnalyzer(version, analyzer).then((a = {}) => ({ ...analyzer, ...a.data }))
            );
            if (!requests.length) return;
            let analyzersDetails = await Promise.all(requests);

            analyzersDetails = analyzersDetails.filter(
                x => ('totalActiveCount' in x ? Boolean(x.totalActiveCount) : true)
            );

            store.dispatch({
                type: DOCUMENT_SCORECARD.FETCH_ANALYZERS_DETAILS,
                payload: analyzersDetails
            });
        }
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'scoreCardActions.fetchAnalyzersDetails', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

export async function addToFavorite({ id }, userId) {
    try {
        addLog(LOG_TYPE.INFO, 'scoreCardActions.addToFavorite');

        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');

        await API_SERVICE.DIGAPP_API({
            method: 'POST',
            url: `${FAVORITES_API}?digitalAppId=${id}&userId=${userId}`
        });

        //trigger action to update favorites apps
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'scoreCardActions.addToFavorite', err);

        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: 'Some thing Bad happen unable to add digital App to Favorites '
        });

        throw err;
    }
}

export async function deleteFromFavorite({ id }, userId) {
    try {
        addLog(LOG_TYPE.INFO, 'scoreCardActions.deleteFromFavorite');

        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');

        await API_SERVICE.DIGAPP_API({
            method: 'DELETE',
            url: `${FAVORITES_API}?digitalAppId=${id}&userId=${userId}`
        });

        //trigger action to remove it from  favorites apps
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'scoreCardActions.deleteFromFavorite', err);

        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: 'Some thing Bad happen unable to remove digital App from Favorites '
        });

        throw err;
    }
}

export async function getFavorites(userId) {
    try {
        addLog(LOG_TYPE.INFO, 'scoreCardActions.getFavorites');

        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');

        const { data } = await API_SERVICE.DIGAPP_API({
            method: 'GET',
            url: `${FAVORITES_API}?userId=${userId}`
        });

        return data.data;

        //trigger action to add new favorite app to favorites list
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'scoreCardActions.getFavorites');

        return [];
    }
}

export async function fetchAnalyzer(version, { id, name }) {
    addLog(
        LOG_TYPE.INFO,
        'scoreCardActions.fetchAnalyzer',
        `DigiApp Version=${version} on digAppId=${id}, digAppName=${name}`
    );

    const versionedEndPointUrl = getAPIByVersion('scorecardRules', version);
    const endPointUrl = versionedEndPointUrl.replace('{digAppId}', id);

    addLog(LOG_TYPE.INFO, 'scoreCardActions.fetchAnalyzer', endPointUrl);

    API_SERVICE.refresh();
    API_SERVICE.setContentType('application/json');
    try {
        const res = await API_SERVICE.DIGAPP_API.get(endPointUrl);

        return res;
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'scoreCardActions.fetchAnalyzer', err);
        return {};
    }
}

function filterForActiveAnalyzers(version, data) {
    let activeAnalyzers;
    if (isAfterOrEqual('7.6.0.0', version)) {
        activeAnalyzers = data.filter(
            digApp =>
                digApp.scorecardRuleActiveCount > 0 &&
                digApp.isActive &&
                (digApp.schedulerEnabled === undefined || !digApp.schedulerEnabled)
        );
    } else if (isAfterOrEqual('7.5.0', version)) {
        activeAnalyzers = data.filter(
            digApp =>
                digApp.isActive &&
                (digApp.schedulerEnabled === undefined || !digApp.schedulerEnabled)
        );
    } else {
        activeAnalyzers = data.filter(digApp => digApp.status === 'Enabled');
    }
    return activeAnalyzers;
}

function isDigAppsEnabled(res) {
    try {
        if (res && res.data && res.data.data && res.data.data.length > 0) {
            let enabledSystems = 0;
            let digAppStatusFound = false;
            res.data.data.forEach(digApp => {
                // Only 7.3 systems have the digApp.status
                if (digApp.status !== undefined) {
                    digAppStatusFound = true;
                    if (digApp.status === 'Enabled') enabledSystems += 1; // version < v7.5.0
                }
                // Only 7.5 systems have the digApp.isActive
                if (digApp.isActive !== undefined) {
                    digAppStatusFound = true;
                    if (
                        digApp.isActive === true &&
                        (digApp.schedulerEnabled === undefined || digApp.schedulerEnabled === false)
                    )
                        enabledSystems += 1; // version >= v7.5.0 (scheduler must be disabled for active digiApps)
                }
            });

            return digAppStatusFound ? enabledSystems > 0 : true;
        }

        return false;
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'scoreCardActions.isDigAppsEnabled', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return false;
    }
}

function malConfigurationCheck(res) {
    try {
        if (res && res.data && res.data.data && res.data.data.length > 0) {
            let malconfigurationCount = 0;
            res.data.data.forEach(digApp => {
                // Only 7.5 systems have the digApp.isActive
                if (digApp.isActive !== undefined) {
                    if (
                        digApp.isActive &&
                        digApp.schedulerEnabled !== undefined &&
                        digApp.schedulerEnabled
                    )
                        malconfigurationCount += 1; // version >= v7.5.0 (both cannot be enabled at the same time)
                }
            });

            return malconfigurationCount > 0;
        }

        return false;
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'scoreCardActions.malconfiguration', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return false;
    }
}

export function clearScoreCard() {
    return dispatch => {
        dispatch({ type: DOCUMENT_SCORECARD.CLEAR_SCORECARD });
    };
}

export function getScoreCardPromise(
    digAppName, // digAppName for 7.2.x to 7.4.x backends, digAppId for version > 7.5.x
    version,
    documentId,
    sealVersion,
    scorecardPollingEnabled
) {
    return new Promise(resolve => {
        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');

        if (digAppName === '') {
            // without a digappName this endpoint will fail
            return resolve();
        }

        store.dispatch({ type: DOCUMENT_SCORECARD.FETCH_SCORECARD_REQUEST, payload: digAppName });

        const endPointUrl = getAPIByVersion('scorecard', version);
        addLog(LOG_TYPE.INFO, 'scoreCardActions.getScoreCard', endPointUrl);
        API_SERVICE.DIGAPP_API({
            method: 'get',
            url: endPointUrl,
            params: { contractId: documentId, digApp: digAppName, filter: 'all' }
        })
            .then(res => {
                addLog(LOG_TYPE.INFO, 'scoreCardActions.getScoreCard', {
                    data: {
                        ...res.data.data,
                        contractLink: '****',
                        scoreCardElementList: ['****']
                    }
                });

                // create a physical order attribute
                let physicalOrder = 0;
                res.data.data.scoreCardElementList.forEach(scoreCardElement => {
                    scoreCardElement.physicalOrder = physicalOrder;
                    physicalOrder += 1;
                });

                if (sealVersion) {
                    if (sealVersion.startsWith('7.2.') || sealVersion.startsWith('7.3.')) {
                        // for older versions, calculate the risk level counts then add the new riskMap to the json
                        // likewise, ensure that riskLevel and riskColor is set for each scorecard element
                        calculateRiskLevels(res.data.data);
                    }
                }

                // Set a unique id for the incoming data
                res.data.data.scoreCardElementList.forEach(scoreCardElement => {
                    scoreCardElement.uid = guid();
                });

                // cleanse the metadata values of all the unwanted noise to assist the click-to-navigate
                res.data = prepMetadataValues(res.data);

                const rootReducer = store.getState();
                const { document } = rootReducer;
                const { documentStatus } = document;
                if (documentStatus === DocumentStatus.NOT_SUBMITTED) {
                    // abort
                    return resolve();
                }

                store.dispatch({
                    type: DOCUMENT_SCORECARD.FETCH_SCORECARD_SUCCESS,
                    payload: res.data
                });

                store.dispatch({ type: NOTIFICATION_HANDLER.DISMISS_ERROR });
                return resolve();
            })
            .catch(err => {
                if (
                    err.response &&
                    err.response.data &&
                    // Status code check for seal versions <= 7.5.0 and versions > 7.5.0
                    (err.response.data.code === 1000402 || err.response.data.code === 104202)
                ) {
                    const failureMsg =
                        err.response && err.response.data ? err.response.data.message : '';

                    addLog(
                        LOG_TYPE.INFO,
                        'scoreCardActions.getScoreCard',
                        `DigiApp Version=${version} on digAppName=${digAppName} for docId=${documentId} Code=1000402, message=${failureMsg}`
                    );

                    if (scorecardPollingEnabled) {
                        store.dispatch({
                            type: DOCUMENT_SCORECARD.TOGGLE_OFF_PULLING
                        });

                        store.dispatch({
                            type: DOCUMENT_SCORECARD.FETCH_SCORECARD_PENDING
                        });
                    } else {
                        // we still need to fetch the metaata even if all the digApps are deactivated
                        refreshMetaData();
                        abortAnimation();
                    }
                } else {
                    handleScoreCardError(digAppName, err, 'scoreCardActions.getScoreCard');
                }

                return resolve();
            });
    });
}

export function getScoreCard(
    digAppName, // digAppName for 7.2.x to 7.4.x backends, digAppId for version > 7.5.x
    version,
    documentId,
    sealVersion,
    scorecardPollingEnabled
) {
    return dispatch => {
        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');

        dispatch({ type: DOCUMENT_SCORECARD.FETCH_SCORECARD_REQUEST, payload: digAppName });

        const endPointUrl = getAPIByVersion('scorecard', version);
        addLog(LOG_TYPE.INFO, 'scoreCardActions.getScoreCard', endPointUrl);
        API_SERVICE.DIGAPP_API({
            method: 'get',
            url: endPointUrl,
            params: { contractId: documentId, digApp: digAppName, filter: 'all' }
        })
            .then(res => {
                addLog(LOG_TYPE.INFO, 'scoreCardActions.getScoreCard', {
                    data: {
                        ...res.data.data,
                        contractLink: '****',
                        scoreCardElementList: ['****']
                    }
                });

                // create a physical order attribute
                let physicalOrder = 0;
                res.data.data.scoreCardElementList.forEach(scoreCardElement => {
                    scoreCardElement.physicalOrder = physicalOrder;
                    physicalOrder += 1;
                });

                if (sealVersion) {
                    if (sealVersion.startsWith('7.2.') || sealVersion.startsWith('7.3.')) {
                        // for older versions, calculate the risk level counts then add the new riskMap to the json
                        // likewise, ensure that riskLevel and riskColor is set for each scorecard element
                        calculateRiskLevels(res.data.data);
                    }
                }

                // Set a unique id for the incoming data
                res.data.data.scoreCardElementList.forEach(scoreCardElement => {
                    scoreCardElement.uid = guid();
                });

                // cleanse the metadata values of all the unwanted noise to assist the click-to-navigate
                res.data = prepMetadataValues(res.data);

                const rootReducer = store.getState();
                const { document } = rootReducer;
                const { documentStatus } = document;
                if (documentStatus === DocumentStatus.NOT_SUBMITTED) {
                    // abort
                    return;
                }

                dispatch({
                    type: DOCUMENT_SCORECARD.FETCH_SCORECARD_SUCCESS,
                    payload: res.data
                });

                // Now that the scorecard data is complete we should refresh the Labels data so that we can show
                // z-fields
                store.dispatch({ type: NOTIFICATION_HANDLER.DISMISS_ERROR });
                refreshMetaData();
            })
            .catch(err => {
                if (
                    err.response &&
                    err.response.data &&
                    // Status code check for seal versions <= 7.5.0 and versions > 7.5.0
                    (err.response.data.code === 1000402 || err.response.data.code === 104202)
                ) {
                    const failureMsg =
                        err.response && err.response.data ? err.response.data.message : '';

                    addLog(
                        LOG_TYPE.INFO,
                        'scoreCardActions.getScoreCard',
                        `DigiApp Version=${version} on digAppName=${digAppName} for docId=${documentId} Code=1000402, message=${failureMsg}`
                    );

                    if (scorecardPollingEnabled) {
                        dispatch({
                            type: DOCUMENT_SCORECARD.TOGGLE_OFF_PULLING
                        });

                        dispatch({
                            type: DOCUMENT_SCORECARD.FETCH_SCORECARD_PENDING
                        });
                    } else {
                        // we still need to fetch the metaata even if all the digApps are deactivated
                        refreshMetaData();
                        abortAnimation();
                    }
                } else {
                    handleScoreCardError(digAppName, err, 'scoreCardActions.getScoreCard');
                }
            });
    };
}

// This code only applies to seal backend v7.2.1 and 7.3.x
function calculateRiskLevels(data) {
    try {
        let lowRiskCount = 0;
        let mediumLowRiskCount = 0;
        let mediumRiskCount = 0;
        let highRiskCount = 0;
        let naRiskCount = 0;
        data.scoreCardElementList.forEach(scoreCardElement => {
            const { answerField } = scoreCardElement;
            const { values } = answerField;
            let color = '';
            if (values && values.length > 0) {
                const { attributes } = values[0];

                if (attributes && attributes.length > 0) {
                    attributes.forEach(attribute => {
                        if (attribute.name.toLowerCase() === 'color') {
                            color = attribute.value;
                        }

                        if (attribute.name.toLowerCase() === 'atn_color') {
                            color = attribute.value;
                        }
                    });
                }
            }

            if (color === 'Red') {
                highRiskCount += 1;
                scoreCardElement.risk = 'High';
                scoreCardElement.riskSeverity = 1; // used for sorting
                scoreCardElement.riskLevel = 'high';
                scoreCardElement.riskColor = '#c93e03';
            }

            if (color === 'Yellow') {
                mediumRiskCount += 1;
                scoreCardElement.risk = 'Medium';
                scoreCardElement.riskSeverity = 2; // used for sorting
                scoreCardElement.riskLevel = 'medium';
                scoreCardElement.riskColor = '#fdd553';
            }

            if (color === 'Blue') {
                mediumLowRiskCount += 1;
                scoreCardElement.risk = 'Mid-low';
                scoreCardElement.riskSeverity = 3; // used for sorting
                scoreCardElement.riskLevel = 'mid-low';
                scoreCardElement.riskColor = '#0593B6';
            }

            if (color === 'Green') {
                lowRiskCount += 1;
                scoreCardElement.risk = 'Low';
                scoreCardElement.riskSeverity = 4; // used for sorting
                scoreCardElement.riskLevel = 'low';
                scoreCardElement.riskColor = '#7dbf64';
            }

            if (color === '') {
                naRiskCount += 1;
                scoreCardElement.risk = 'N/A';
                scoreCardElement.riskSeverity = 999; // used for sorting
                scoreCardElement.riskLevel = 'N/A';
                scoreCardElement.riskColor = '#808080';
            }
        });

        if (mediumLowRiskCount > 0) {
            data.high = highRiskCount;
            data.mediumLowRisk = mediumLowRiskCount;
            data.mediumRisk = mediumRiskCount;
            data.lowRisk = lowRiskCount;
        } else {
            data.mediumLowRisk = 0;
        }

        /* eslint-disable prettier/prettier */
        data.riskMap = {
            high: {
                color: '#c93e03',
                count: highRiskCount,
                severity: 1
            },
            medium: {
                color: '#fdd553',
                count: mediumRiskCount,
                severity: 2
            },
            'mid-low': {
                color: '#0593B6',
                count: mediumLowRiskCount,
                severity: 3
            },
            low: {
                color: '#7dbf64',
                count: lowRiskCount,
                severity: 4
            },
            'n/a': {
                color: '#808080',
                count: naRiskCount,
                severity: 999
            }
        };
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'scoreCardActions.calculateRiskLevels', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

function prepMetadataValues(data) {
    try {
        if (
            data.data &&
            data.data.scoreCardElementList &&
            data.data.scoreCardElementList.length > 0
        ) {
            data.data.scoreCardElementList.forEach(scoreCardElement => {
                if (
                    scoreCardElement &&
                    scoreCardElement.analytics &&
                    scoreCardElement.analytics.length > 0
                ) {
                    scoreCardElement.analytics.forEach(analytic => {
                        if (analytic && analytic.values && analytic.values.length > 0) {
                            analytic.values.forEach(value => {
                                if (value && value.value && value.value.length > 0) {
                                    if (value.value !== 'No extraction') {
                                        value.value = useOriginalValue(value);
                                    }
                                    value.value = cleanMetadataValue(value.value);
                                }
                            });
                        }
                    });
                }
            });
        }

        return data;
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'scoreCardActions.prepMetadataValues', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return data;
    }
}

function handleDigAppError(err, method) {
    try {
        addLog(LOG_TYPE.ERROR, method, err);
        let failureMsg = err.response && err.response.data ? err.response.data.message : '';
        if (err.response && err.response.status && err.response.status === 404) {
            failureMsg =
                'There is a problem connecting to the Seal services right now. If something does not seem to work as expected please contact your system administrator.';
        } else if (err.response && err.response.status && err.response.status === 401) {
            failureMsg = 'Unauthorized';
        } else if (
            err.response &&
            err.response.status &&
            err.response.status !== 404 &&
            err.response.status !== 401
        ) {
            failureMsg =
                'Failed to load analyzer, please mail the Seal support team and include the error log using the copy icon below.';
        }

        if (err.toString().includes('timeout')) {
            failureMsg = 'Unable to connect to scorecard service';
        }

        store.dispatch({ type: DOCUMENT_SCORECARD.FETCH_DIGAPP_FAILURE, payload: failureMsg });
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'scoreCardActions.handleDigAppError', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: error
        });
    }
}

function handleScoreCardError(digAppName, err, method) {
    try {
        addLog(LOG_TYPE.ERROR, method, err);

        let failureMsg = err.response && err.response.data ? err.response.data.message : '';
        if (failureMsg.includes('not found')) {
            store.dispatch({
                type: DOCUMENT_METADATA.SWITCH_SYSTEM,
                payload: { isSubmitted: false }
            });

            return;
        }

        if (err.response && err.response.status && err.response.status !== 404) {
            failureMsg =
                'Failed to load scorecard, please mail the Seal support team and include the error log using the copy icon below.';
        }
        if (err.response && err.response.data && err.response.data.code === 1000901) {
            // check to see if this analyzer has subsequently been disabled
            const rootReducer = store.getState();
            const { scoreCard } = rootReducer;
            const { digAppsFilteredArray } = scoreCard;
            const findDigApp = Object.values(digAppsFilteredArray).filter(
                app => app.text === digAppName
            );
            if (findDigApp && findDigApp.length > 0) {
                failureMsg =
                    "The scorecard analyzer can't be found, check if it's properly configured then try again.";
            } else {
                store.dispatch({
                    type: NOTIFICATION_HANDLER.WARNING,
                    payload: 'Analyzer no longer available'
                });

                abortAnimation();
                return;
            }
        }

        if (failureMsg === '') {
            failureMsg = err.toString();
        }

        store.dispatch({
            type: DOCUMENT_SCORECARD.FETCH_SCORECARD_FAILURE,
            payload: failureMsg
        });
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'scoreCardActions.handleScoreCardError', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: error
        });
    }
}
